import { useState, useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { auth, logoutUser } from './utils/firebase';
import CampaignForm from './components/CampaignForm';
import ApiKeyForm from './components/ApiKeyForm';
import CampaignList from './components/CampaignList';
import WorkflowPage from './pages/WorkflowPage';
import { CampaignFormData } from './types';

function App() {
  const [userId, setUserId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CampaignFormData | null>(null);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  
  const handleAuthSubmit = (uid: string) => {
    setUserId(uid);
  };
  
  const handleFormSubmit = (data: CampaignFormData) => {
    setFormData(data);
    localStorage.setItem('campaign_form_data', JSON.stringify(data));
  };
  
  const handleReset = () => {
    if (confirm('Are you sure you want to start a new campaign? This will clear all current data.')) {
      setFormData(null);
      setShowNewCampaign(false);
      localStorage.removeItem('campaign_form_data');
    }
  };

  const handleSelectCampaign = (campaign: CampaignFormData) => {
    setFormData(campaign);
    localStorage.setItem('campaign_form_data', JSON.stringify(campaign));
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setUserId(null);
      setFormData(null);
      setShowNewCampaign(false);
      localStorage.removeItem('campaign_form_data');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setFormData(null);
        setShowNewCampaign(false);
      }
    });
    
    // Try to get form data from local storage
    const storedFormData = localStorage.getItem('campaign_form_data');
    if (storedFormData) {
      try {
        setFormData(JSON.parse(storedFormData));
      } catch (e) {
        console.error('Error parsing stored form data', e);
        localStorage.removeItem('campaign_form_data');
      }
    }
    
    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-red-600 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Logo with border frame inspired by Reveille Strategy */}
              <div className="border-2 border-white p-4 mr-4">
                <div className="text-xl font-bold tracking-wider">
                  UNION POLITICAL
                  <br />
                  CAMPAIGN PLANNER
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {userId && !showNewCampaign && !formData && (
                <button
                  onClick={() => setShowNewCampaign(true)}
                  className="px-6 py-2 bg-red-700 hover:bg-red-800 rounded-md transition-colors font-medium"
                >
                  New Campaign
                </button>
              )}
              {userId && (
                <button
                  onClick={handleLogout}
                  className="px-6 py-2 bg-red-700 hover:bg-red-800 rounded-md transition-colors font-medium"
                >
                  Sign Out
                </button>
              )}
              {formData && (
                <button
                  onClick={handleReset}
                  className="px-4 py-2 text-sm bg-red-700 hover:bg-red-800 rounded-md font-medium"
                >
                  New Campaign
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto py-8 px-6">
        {!userId ? (
          <div className="max-w-md mx-auto">
            <ApiKeyForm onSubmit={handleAuthSubmit} />
          </div>
        ) : showNewCampaign ? (
          <CampaignForm onSubmit={handleFormSubmit} />
        ) : !formData ? (
          <CampaignList userId={userId} onSelectCampaign={handleSelectCampaign} />
        ) : (
          <WorkflowPage 
            formData={formData} 
            userId={userId}
            onReset={handleReset}
          />
        )}
      </main>
      
      {/* Footer */}
      <footer className="bg-neutral-800 text-neutral-300 py-8 mt-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center">
                <div className="border border-neutral-500 p-2 mr-3">
                  <span className="text-white font-bold text-sm tracking-wider">UPC</span>
                </div>
                <span className="text-white font-semibold">Union Political Campaign Planner</span>
              </div>
              <p className="text-sm mt-2">Strategic campaign planning for political action</p>
            </div>
            <div className="text-sm text-center md:text-right">
              <p>© 2025 Union Political Campaign Planner</p>
              <p>Powered by AI Strategy Analysis</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
